import type { StorageBackend } from '@ext/storage/index';

/**
 * The website's storage for cards, known words, review history and decks: one IndexedDB database
 * with one object store, key to value, values kept as structured clones. Unlike localStorage
 * (about 5 MB) it holds a whole collection with its pictures' references.
 *
 * If the browser refuses IndexedDB (some private modes), values are kept in memory for this tab:
 * the page still works, but nothing survives a reload.
 */
export interface IdbStore extends StorageBackend {
  /** Removes everything. */
  clear(): Promise<void>;
}

const DB_NAME = 'lingoping';
const STORE = 'kv';

export function createIdbStore(
  factory: IDBFactory | undefined = typeof indexedDB === 'undefined' ? undefined : indexedDB,
  dbName: string = DB_NAME
): IdbStore {
  const memory = new Map<string, unknown>();
  let opening: Promise<IDBDatabase | null> | null = null;

  function open(): Promise<IDBDatabase | null> {
    if (opening) return opening;
    opening = new Promise<IDBDatabase | null>((resolve) => {
      if (!factory) return resolve(null);
      try {
        const request = factory.open(dbName, 1);
        request.onupgradeneeded = () => request.result.createObjectStore(STORE);
        request.onsuccess = () => {
          const db = request.result;
          // Another tab upgrading or deleting the database must not be blocked by this one.
          db.onversionchange = () => {
            db.close();
            opening = null;
          };
          resolve(db);
        };
        request.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    }).then((db) => {
      if (!db) console.warn('[LingoPing] IndexedDB is not available; keeping data in memory.');
      return db;
    });
    return opening;
  }

  async function run<T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    const db = await open();
    if (!db) throw new Error('no database');
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = action(tx.objectStore(STORE));
      // Resolve when the transaction is committed, so a value is on disk when `set` returns.
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    });
  }

  return {
    async get(key) {
      if (!(await open())) return structuredClone(memory.get(key));
      return run('readonly', (store) => store.get(key));
    },

    async set(key, value) {
      if (!(await open())) {
        memory.set(key, structuredClone(value));
        return;
      }
      await run('readwrite', (store) => store.put(value, key));
    },

    async clear() {
      memory.clear();
      if (!(await open())) return;
      await run('readwrite', (store) => store.clear());
    },
  };
}
